import DecimalBase from 'decimal.js';

/**
 * Decimal configuré pour ExpertDevis BTP : 28 chiffres significatifs, arrondi commercial (half-up).
 * Toute grandeur (quantité, coefficient, montant) transite par ce type — jamais par `number`
 * pour un calcul, afin que 0,1 + 0,2 = 0,3 exactement.
 */
export const Decimal = DecimalBase.clone({
  precision: 28,
  rounding: DecimalBase.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 30,
});
export type Decimal = InstanceType<typeof Decimal>;

export type DecimalInput = Decimal | number | string;

export function D(value: DecimalInput): Decimal {
  if (value instanceof Decimal) return value;
  if (typeof value === 'string') return new Decimal(value.trim().replace(',', '.'));
  if (!Number.isFinite(value)) throw new RangeError(`Valeur numérique invalide : ${value}`);
  return new Decimal(value);
}

export const ZERO = new Decimal(0);
export const ONE = new Decimal(1);

/** Arrondi half-up à `decimals` décimales. */
export function roundTo(value: Decimal, decimals: number): Decimal {
  return value.toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP);
}

/** Arrondi à l'unité supérieure (quantités d'achat : sacs, barres, blocs…). */
export function ceilTo(value: Decimal, step: DecimalInput = 1): Decimal {
  const s = D(step);
  return value.div(s).ceil().mul(s);
}

/** Somme exacte. */
export function sum(values: Iterable<Decimal>): Decimal {
  let total = ZERO;
  for (const v of values) total = total.plus(v);
  return total;
}

export interface FormatOptions {
  /** Nombre minimal de décimales affichées (zéros conservés). */
  minDecimals?: number;
  /** Nombre maximal de décimales (arrondi half-up). */
  maxDecimals?: number;
  /** Séparateur des milliers (par défaut espace fine insécable U+202F). */
  thousandsSeparator?: string;
  decimalSeparator?: string;
}

/**
 * Formatage français déterministe (indépendant de l'ICU de la plateforme) :
 * 20785707 → « 20 785 707 », 1.2 → « 1,20 » (minDecimals 2).
 */
export function formatNumber(value: DecimalInput, options: FormatOptions = {}): string {
  const { minDecimals = 0, maxDecimals = 6, thousandsSeparator = ' ', decimalSeparator = ',' } = options;
  const rounded = roundTo(D(value), maxDecimals);
  const negative = rounded.isNegative() && !rounded.isZero();
  const fixed = rounded.abs().toFixed(maxDecimals);
  let [intPart = '0', fracPart = ''] = fixed.split('.');
  fracPart = fracPart.replace(/0+$/, '');
  if (fracPart.length < minDecimals) fracPart = fracPart.padEnd(minDecimals, '0');
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  return (negative ? '−' : '') + intPart + (fracPart ? decimalSeparator + fracPart : '');
}
